/**
 * Board Piece Set Models — the list, once.
 *
 * A Board game is played with physical pieces, and the build ships the vision model that
 * recognises them. Which model that is, is **declared per build** by the uploader
 * (`CreateBuildRequest.pieceModel`), exactly like `versionName` is: nothing here is read out of
 * an artifact, and `apps/api/src/lib/artifact.ts` still reads exactly one fact and it is not
 * this one.
 *
 * The catalog is code rather than a table for the same reason `FEATURES` is: new sets ship with
 * a release, and a row nobody can add at runtime is a row every reader already knows how to
 * draw. This is the list, and the web kit's decoration of it
 * (`apps/web/src/kit/playground/models.ts`) follows it. (There was a third copy, a Kotlin twin in
 * the Board client, with no codegen bridge between the two languages; it went with the client.)
 *
 * Tints and tile art are **not** here. They are CSS variables and Vite-public paths, which the
 * Worker has no use for, and a registry carrying them would be a registry its readers could
 * disagree with. Same rule as `FEATURES`: the registry is the list, and whoever draws it
 * decorates it.
 *
 * The user-facing name for all of this is the design's — **Board Piece Model**. Internal
 * identifiers say `pieceModel`.
 */
import { z } from 'zod';
import { PLAYGROUND_CHANNEL_SLUG, PUBLIC_CHANNEL_SLUG } from './store.js';
/**
 * The shape of a declared model stem, on every write path that takes one.
 *
 * Deliberately **not** an enum over `PIECE_MODELS`: a developer may ship a model of their own that
 * no release has ever heard of, and refusing a build or a channel edit over a name would be
 * refusing it over this deployment's age. The shape is checked, the vocabulary is not — the read
 * path prettifies a stem with no row and draws it without art.
 *
 * One definition rather than the regex written out at `CreateBuildRequest.pieceModel`,
 * `UpdateChannelRequest.primaryModel` and `supportedModels`, for the same reason the table above
 * is one table.
 */
export const PieceModelStem = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9_]{1,80}$/);
const piece = (glyphId, name, ...aliases) => ({
    glyphId,
    name,
    aliases,
});
/**
 * The ten models `playground/models/` carries, keyed by stem.
 *
 * **The key is the stem, not a parse of it.** Parsing cannot work: `chop_chop_mushka` is two
 * models joined and `save_the_bloogs` is one model with underscores in it, and nothing in either
 * string distinguishes them. Keying on the version-stripped stem is also what survives a reissue
 * — `chop_chop_v1.4.0` resolves through the same `chop_chop` entry with no edit here.
 *
 * `sets` is the fact a `display` cannot carry: a combined model needs *both* physical sets on the
 * table, which is why owning `thrasos` and `arcade` is what satisfies `thrasos_arcade`. Every
 * plain model is a singleton over its own key, and that is not a derivation to skip the field
 * for — the two combined rows are exactly why the field is written out per row.
 *
 * **`pieces` is which `glyphId` is which Piece**, measured on hardware, because Board publishes no
 * such table. It is served at `GET /v1/piece-models` so an agent outside the playground can map a
 * Piece too (P3 Step 2), and the playground's `board-pieces` skill carries the same tables as prose
 * for the agent inside it. The two are one list written twice, and
 * `apps/api/test/piece-models-registry.test.ts` fails the moment they disagree. A model nobody has
 * measured is `pieces: null` here and sits in the skill's *Not mapped yet* list.
 */
export const PIECE_MODELS = {
    // Measured on `arcade_v1.3.7.tflite`. The wedges are not numbered in the robots' colour order.
    arcade: {
        key: 'arcade',
        display: 'Board Arcade',
        sets: ['arcade'],
        pieces: [
            piece(1, 'yellow robot'),
            piece(2, 'purple robot'),
            piece(3, 'orange robot'),
            piece(4, 'pink robot'),
            piece(5, 'pink wedge'),
            piece(6, 'yellow wedge'),
            piece(7, 'purple wedge'),
            piece(8, 'orange wedge'),
        ],
    },
    board_bases: { key: 'board_bases', display: 'Board Bases', sets: ['board_bases'], pieces: null },
    chop_chop: { key: 'chop_chop', display: 'Chop Chop', sets: ['chop_chop'], pieces: null },
    // `heist_v0.3.0.tflite`.
    heist: {
        key: 'heist',
        display: 'Heist',
        sets: ['heist'],
        pieces: [piece(1, 'mirror', 'arc'), piece(2, 'square', 'tunnel', 'splitter', 'merger')],
    },
    // `mushka_v1.3.1.tflite`. Each Piece is a tool, and people call most of them two ways.
    mushka: {
        key: 'mushka',
        display: 'Mushka',
        sets: ['mushka'],
        pieces: [
            piece(1, 'watering can'),
            piece(2, 'bag', 'sack'),
            piece(3, 'hairdryer', 'blower'),
            piece(4, 'sponge', 'brush'),
            piece(5, 'magnifying glass', 'bubble wand'),
        ],
    },
    omakase: { key: 'omakase', display: 'Omakase', sets: ['omakase'], pieces: null },
    save_the_bloogs: {
        key: 'save_the_bloogs',
        display: 'Save the Bloogs',
        sets: ['save_the_bloogs'],
        pieces: null,
    },
    // `thrasos_v2.4.3.tflite`. The god is the Piece's identity and the object is how it looks, so the
    // name is the god. The ids start at 3: no Thrasos Piece reports 1 or 2.
    thrasos: {
        key: 'thrasos',
        display: 'Thrasos',
        sets: ['thrasos'],
        pieces: [
            piece(3, 'Hades', 'Kerberos', 'Cerberus'),
            piece(4, 'Dionysus', 'wine cup', 'kantharos'),
            piece(5, 'Demeter', 'cornucopia', 'horn of plenty'),
            piece(6, 'Athena', 'Athenian coin'),
            piece(7, 'Poseidon', 'trident'),
            piece(8, 'Zeus', 'lightning bolt', 'thunderbolt'),
        ],
    },
    chop_chop_mushka: {
        key: 'chop_chop_mushka',
        display: 'Chop Chop + Mushka',
        sets: ['chop_chop', 'mushka'],
        pieces: null,
    },
    thrasos_arcade: {
        key: 'thrasos_arcade',
        display: 'Thrasos + Arcade',
        sets: ['thrasos', 'arcade'],
        pieces: null,
    },
};
/**
 * The priority-1 model a game has before anybody says otherwise (migrations 0038 and 0063).
 *
 * Written here rather than spelled `'arcade'` at the call sites that need it, for the reason
 * `PIECE_MODELS` exists at all — and asserted against the registry by the shared tests, so a
 * release that renamed the stem could not ship a default pointing at nothing.
 *
 * It is a *default*, not a fallback: `apps.primary_model` is NOT NULL, so by the time anything
 * reads a game the value is whatever that team chose. Nothing downstream may substitute this
 * constant for a missing primary, because a missing primary is a bug rather than a state.
 */
export const DEFAULT_PIECE_MODEL = 'arcade';
/**
 * Every physical piece set any model needs, sorted, derived from the table above.
 *
 * Derived rather than written out, because a set that no model asks for is a set nobody can
 * play with, and a second hand-kept list is the thing the one-list rule exists to prevent.
 *
 * **A set key and a model key are the same namespace, and the eight singletons are the eight
 * boxes.** `chop_chop` names a model *and* the set it needs; `chop_chop_mushka` is a model that
 * needs two of those boxes and is not a box itself. That correspondence is what lets a client
 * render a set's name and art by looking the key up in `PIECE_MODELS` — see `pieceSetDisplay`.
 * If a set ever ships without a model of its own, that lookup is where it breaks, and the fix is
 * a row here rather than a second table.
 */
export const PIECE_SETS = [
    ...new Set(Object.values(PIECE_MODELS).flatMap((model) => model.sets)),
].sort();
/**
 * What to call one physical piece set.
 *
 * Goes through the model of the same key (see `PIECE_SETS`), and prettifies anything with no row
 * — a device may report a set from a newer release than this deployment has heard of, and a
 * count under its own name is better than a count under "unknown".
 */
export function pieceSetDisplay(key) {
    const known = PIECE_MODELS[key];
    if (known)
        return known.display;
    return key
        .split('_')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
/**
 * `chop_chop_v1.3.0.tflite` → `chop_chop`.
 *
 * The extension goes first so a version-less `chop_chop.tflite` still resolves, and the version
 * suffix is matched at the end only — a model whose *name* contained something version-shaped
 * would otherwise lose half of itself.
 *
 * Takes a filename or a bare stem, because both arrive: the CLI reads a `.tflite` filename out of
 * `harness-config.json` and an uploader types the stem on the flag.
 */
export function pieceModelStem(value) {
    return value
        .replace(/\.tflite$/i, '')
        .replace(/_v\d+(\.\d+)*$/i, '')
        .toLowerCase();
}
/**
 * The registry row for a declared model, or null.
 *
 * Tolerant, like `channelColorOf`: an unknown stem is **not** an error anywhere. A developer may
 * ship a model of their own that no release has ever heard of, and an old client may meet a new
 * official one — both of those are a name a client prettifies and draws without art, not a
 * refusal. Null in means null out, which is the not-declared case.
 */
export function pieceModelOf(value) {
    if (!value)
        return null;
    return PIECE_MODELS[pieceModelStem(value)] ?? null;
}
/**
 * A game's model list as stored: **in priority order**, each stem once (P4).
 *
 * The order is the whole of the game's choice. Priority 1 is what the server still calls the primary
 * (`apps.primary_model`, `primaryModel` on the wire): the sibling a build that declared nothing is
 * taken to be, and what a Board that never said which pieces it owns installs. It is written beside
 * the list rather than replacing it because every reader written since P2 reads the field.
 *
 * Every writer goes through this, so "the primary is first" is a property of the column rather than
 * of whoever last wrote it, and a reader can take `list[0]` without checking. Naming a primary that
 * is further down the list moves it to the top — which is what an older dashboard tab or sandbox
 * means when it sends one.
 */
export function gamePieceModels(models, primary) {
    return [...new Set([primary, ...models])];
}
/**
 * How a channel decides which of the game's models a Board installs (P4, migration 0064).
 *
 * - `top` — **Top priority only.** The channel offers one model: the highest-priority one it holds
 *   a build for. Every Board installs that, and a Board known to lack its pieces is refused, as any
 *   install is. Enforced by the server as well as by whoever installs ({@link deviceOffering}
 *   cuts the other siblings out of what a library read offers), so it holds whatever asks.
 * - `auto` — **Automatic.** Each Board installs the highest-priority model whose pieces it has; a
 *   Board that never said what it owns gets the top one. Never asks.
 * - `pick` — **Player picks.** The Board asks which model to install, from every model the channel
 *   holds a build for — pieces or not, because this is how a team tries one — and an installed game
 *   can be changed to another with a reinstall.
 *
 * A channel setting, not a feature key: free, like the model intro, and reversible by the same
 * control. `auto` and `pick` are applied where the install is asked for — the library's pages,
 * with {@link resolveInstall} below — because the inventory they are judged against is one Board's
 * answer, kept with the browser that was given it. (It was the Board client's to apply, when the
 * inventory never left the Board: migration 0037.)
 */
export const MODEL_CHOICES = ['top', 'auto', 'pick'];
export const ModelChoice = z.enum(MODEL_CHOICES);
/**
 * What a new channel starts on — the one rule, beside `updateNoticeDefault`, written here rather
 * than left to the column DEFAULT so it is not half in SQL.
 *
 * `public` is strangers walking up to a Board, so it gets the model their pieces fit. `internal` and
 * `playground` are the team's own, and trying every model is what they are for. Every other channel
 * is a playtest a team set up on purpose, and one model is the least surprising thing to hand a
 * tester; the team widens it in the channel's Settings.
 */
export function modelChoiceDefault(slug) {
    if (slug === PUBLIC_CHANNEL_SLUG)
        return 'auto';
    if (slug === 'internal' || slug === PLAYGROUND_CHANNEL_SLUG)
        return 'pick';
    return 'top';
}
/**
 * The stored value, or `top` for anything this build has never heard of.
 *
 * Tolerant like `channelColorOf`: the column has no CHECK, so the only way to a value outside the
 * three is an operator's `UPDATE` or a newer release, and offering one model is the reading that can
 * never put a sibling on a Board its channel did not mean to offer.
 */
export function modelChoiceOf(value) {
    const parsed = ModelChoice.safeParse(value);
    return parsed.success ? parsed.data : 'top';
}
/**
 * What one channel offers, derived from its game's list, the builds it holds and its model choice
 * (P3 Step 0, P4).
 *
 * Nothing about a channel's models is stored: a channel offers the game's models that have a ready
 * build on it, in priority order. Its `primaryModel` is the first of those — the game's priority 1
 * when this channel has a build for it, otherwise the highest-priority model it does have — so an
 * unconfigured Board is always pointed at something installable. A channel with no build for any of
 * them offers the game's priority 1 alone, which is what a store page describes before anything
 * ships.
 *
 * Under `top` the offer is that first model and nothing else: the other siblings stay on the channel
 * and a developer still sees them, but no Board is offered them.
 *
 * `built` is the model keys of the channel's ready builds, with a build that declared nothing
 * already counted as the game's priority 1. A built model the game no longer lists is not offered: it
 * was taken off the menu, and an installer must never offer what the menu does not.
 *
 * One implementation for the Worker's three serializers and any client that wants to say the same
 * sentence, for the reason `resolveFeature` is one function.
 */
export function channelOffering(game, built, choice) {
    const has = new Set(built);
    const offered = gamePieceModels(game.pieceModels, game.primaryModel).filter((model) => has.has(model));
    const top = offered[0] ?? game.primaryModel;
    return { primaryModel: top, supportedModels: choice === 'top' || offered.length === 0 ? [top] : offered };
}
/**
 * The builds a **device** may install from one channel, cut from its current siblings (P3 Step 0, P4).
 *
 * `rows` is the newest build per model, newest first and with the priority-1 sibling first at a tied
 * version code — the order every channel query returns. What comes back:
 *
 * - `modelBuilds`: one build per offered model, in priority order. **Orphans are cut** (a model the
 *   game has since dropped), and under `top` everything but the top model is cut too.
 * - `latest`: the first offered row in the query's order. It is what a reader that knows nothing
 *   of siblings installs — every Board client released before P3 did — so it is cut the same way:
 *   on a `top` channel such a reader gets the top model and nothing else.
 *
 * Generic over the row because the library and the store select different columns; one
 * implementation, so the two device reads cannot come to disagree about what a channel offers.
 */
export function deviceOffering(game, rows, modelOf, choice) {
    const listed = new Set(gamePieceModels(game.pieceModels, game.primaryModel));
    const current = rows.filter((row) => listed.has(modelOf(row)));
    const offering = channelOffering(game, current.map(modelOf), choice);
    const offered = new Set(offering.supportedModels);
    const kept = current.filter((row) => offered.has(modelOf(row)));
    return {
        ...offering,
        modelBuilds: offering.supportedModels
            .map((key) => kept.find((row) => modelOf(row) === key))
            .filter((row) => row !== undefined),
        latest: kept[0] ?? null,
    };
}
/** `GET /v1/piece-models`: the registry, measured tables included. Static and the same for everybody. */
export const PieceModelsResponse = z.object({
    defaultModel: z.string(),
    items: z.array(z.object({
        key: z.string(),
        display: z.string(),
        sets: z.array(z.string()),
        pieces: z
            .array(z.object({ glyphId: z.number().int(), name: z.string(), aliases: z.array(z.string()) }))
            .nullable(),
    })),
});
function inventoryOf(inventory) {
    if (inventory === null || inventory === undefined)
        return null;
    return inventory instanceof Set ? inventory : new Set(inventory);
}
/**
 * The model a build declared, as something to draw — or null when it declared nothing.
 *
 * {@link pieceModelOf} answers null for a stem with no registry row; this answers a row of its own,
 * named by prettifying the stem, with **no sets**. A developer's private model and an official one
 * newer than this release are both drawn by name rather than refused, and the empty `sets` is what
 * keeps {@link piecesCompatible} from ever judging one: a set named after the stem would let a
 * fully stocked Board fail a model nobody here has heard of.
 *
 * Trimmed and blank-checked on both sides of the stem. The server trims on the write path but the
 * column has no CHECK, and `".tflite"` stems to the empty string by a different road.
 */
export function pieceModelNamed(value) {
    const trimmed = value?.trim();
    if (!trimmed)
        return null;
    const stem = pieceModelStem(trimmed);
    if (!stem.trim())
        return null;
    return PIECE_MODELS[stem] ?? { key: stem, display: pieceSetDisplay(stem), sets: [], pieces: null };
}
/**
 * Does this Board have what the game needs?
 *
 * **Three answers, and the third is the point.** `null` is *no judgment*: what a Board that was
 * never asked gets, what a build that declared nothing gets, and what a model with no known boxes
 * gets. Only a Board that has answered, about a model whose boxes are known, gets `true` or `false`.
 *
 * "Not compatible" is a strong thing to say to somebody standing in front of a game. Saying it out
 * of ignorance — because they skipped a question, or because the game ships a model newer than this
 * release — would make every such notice worth less. Subset containment, and nothing cleverer.
 */
export function piecesCompatible(inventory, model) {
    const have = inventoryOf(inventory);
    if (have === null)
        return null;
    if (!model || model.sets.length === 0)
        return null;
    return model.sets.every((set) => have.has(set));
}
/**
 * Which of a model's sets are not on this Board, in the model's own order. Empty when there is no
 * judgment to make.
 */
export function missingPieceSets(inventory, model) {
    if (!model || piecesCompatible(inventory, model) !== false)
        return [];
    const have = inventoryOf(inventory);
    return model.sets.filter((set) => !have.has(set));
}
/**
 * Which sibling a build is: what it declared, or the channel's primary when it declared none.
 *
 * The same coalesce the server partitions on, and the reason every build uploaded before siblings
 * existed still resolves. A channel from a server older than `primaryModel` is `arcade`, which is
 * what migration 0038 backfilled every channel to.
 */
export function modelKeyOf(build, channel) {
    return build.pieceModel ?? channel.primaryModel ?? DEFAULT_PIECE_MODEL;
}
/**
 * A library channel's {@link ModelChoice}, as a device reads it.
 *
 * Not {@link modelChoiceOf}, which reads the stored column and has no "absent": a word nobody here
 * has heard of is `top` either way, but a *missing* one — a server older than the setting — is
 * `auto`, which is how a channel behaved before the setting existed, minus the question.
 */
export function channelModelChoice(channel) {
    if (channel.modelChoice === undefined || channel.modelChoice === null)
        return 'auto';
    return modelChoiceOf(channel.modelChoice);
}
/**
 * The builds an install on this channel chooses between, highest priority first.
 *
 * Priority is the order of `supportedModels`; a build for a model that list does not name goes
 * last. Sorted here rather than trusted from `modelBuilds`, because which sibling a Board gets is
 * the one thing this ordering decides. With no `modelBuilds` — an older server — `latestBuild` is
 * the only thing there is.
 */
function installCandidates(channel) {
    const siblings = channel.modelBuilds ?? [];
    const builds = siblings.length > 0 ? siblings : channel.latestBuild ? [channel.latestBuild] : [];
    const rank = new Map((channel.supportedModels ?? []).map((key, index) => [key, index]));
    const rankOf = (build) => rank.get(modelKeyOf(build, channel)) ?? Number.MAX_SAFE_INTEGER;
    return [...builds].sort((a, b) => rankOf(a) - rankOf(b));
}
/**
 * What to install from this channel onto a Board that does not have the game, and whether to
 * install at all. **The missing-pieces gate.**
 *
 * The channel's {@link ModelChoice} picks the candidates; {@link piecesCompatible} judges them.
 *
 * - `pick` with more than one sibling is `ask`, every sibling, and is never refused: it is a team's
 *   own channel for trying models. With one sibling there is nothing to ask, and it installs.
 * - `top` judges the one model the channel offers.
 * - `auto` takes the highest-priority sibling whose pieces are here.
 *
 * A build stays installable unless its pieces are *known* to be missing. Only `missing` refuses,
 * and it is reachable only when every candidate was judged and every judgment was `false`.
 */
export function resolveInstall(inventory, channel) {
    const all = installCandidates(channel);
    const first = all[0];
    if (!first)
        return { kind: 'unavailable' };
    const have = inventoryOf(inventory);
    const choice = channelModelChoice(channel);
    if (choice === 'pick') {
        if (all.length > 1)
            return { kind: 'ask', builds: all };
        return have === null ? { kind: 'unconfigured', build: first } : { kind: 'ready', build: first };
    }
    // Top priority only: the first candidate and nothing else — the server already cut the rest, and
    // this holds the line for a library read before the setting changed.
    const considered = choice === 'top' ? [first] : all;
    // No inventory, or no menu to judge it against. Both are unanswered rather than incompatible. An
    // empty menu is an older server, and refusing a game because the reader is newer than the answer
    // it got would be the worst kind of judgment.
    if (have === null || (channel.supportedModels ?? []).length === 0) {
        const primary = channel.primaryModel ?? DEFAULT_PIECE_MODEL;
        const fallback = considered.find((build) => modelKeyOf(build, channel) === primary) ?? first;
        return { kind: 'unconfigured', build: fallback };
    }
    // `!== false` and not `=== true`: an unknown model is null, and no judgment is not grounds for
    // refusing an install. Highest priority first.
    const ready = considered.find((build) => piecesCompatible(have, pieceModelNamed(modelKeyOf(build, channel))) !== false);
    if (ready)
        return { kind: 'ready', build: ready };
    return { kind: 'missing', sets: fewestMissing(have, considered.map((build) => pieceModelNamed(modelKeyOf(build, channel)))) };
}
/**
 * The fewest boxes that would let them play, not every box across every model — the notice exists
 * to be acted on. Ties keep the priority order. Never empty in practice: it is asked only once every
 * model was judged false, which needs a model whose sets are known.
 */
function fewestMissing(have, models) {
    let shortest = null;
    for (const model of models) {
        const missing = missingPieceSets(have, model);
        if (missing.length > 0 && (shortest === null || missing.length < shortest.length))
            shortest = missing;
    }
    return shortest ?? [];
}
/**
 * {@link resolveInstall}'s one refusal, judged on what a channel *offers* rather than on its builds:
 * the boxes a Board is missing for a game it does not have, or null when there is nothing to refuse.
 *
 * For the library's cards, which carry the offer and no builds (`StoreCard.pieceModels`). The
 * offer is `channelOffering`'s, so its models are exactly the siblings `resolveInstall` would
 * consider on that channel, in the same order, and the two cannot come to different verdicts:
 *
 * - `pick` is never refused — a team's own channel for trying models.
 * - `top` judges the first model and nothing else.
 * - `auto` refuses only when no model fits.
 *
 * Null is every answer that is not a refusal: the Board fits, it was never asked, or the game names
 * no model this release knows the boxes of. The same three-way honesty as {@link piecesCompatible} —
 * "incompatible" is said only of an answered Board and a known model.
 */
export function offerMissing(inventory, offer) {
    const have = inventoryOf(inventory);
    const models = offer.pieceModels ?? [];
    const choice = channelModelChoice({ modelChoice: offer.modelChoice ?? undefined });
    if (have === null || models.length === 0 || choice === 'pick')
        return null;
    const considered = (choice === 'top' ? models.slice(0, 1) : models).map(pieceModelNamed);
    if (considered.some((model) => piecesCompatible(have, model) !== false))
        return null;
    return fewestMissing(have, considered);
}
/**
 * Which sibling is on the Board: the model key {@link modelKeyOf} would give the build installed.
 *
 * In order of how much is actually known:
 *
 * 1. What the ledger recorded — the model of the build BoardFlight put there.
 * 2. A record that names no model: a build that declared nothing. When this channel still has the
 *    build the record names, that build says which sibling it is, and nothing is assumed.
 * 3. Otherwise the channel's primary — a game installed some other way. The same rule the server
 *    applies to a build that declared nothing.
 */
export function installedModelKey(lastInstall, channel) {
    if (lastInstall?.pieceModel !== null && lastInstall?.pieceModel !== undefined)
        return lastInstall.pieceModel;
    const id = lastInstall?.buildId;
    const named = id === undefined
        ? undefined
        : ((channel.modelBuilds ?? []).find((build) => build.id === id) ??
            (channel.latestBuild?.id === id ? channel.latestBuild : undefined));
    return named ? modelKeyOf(named, channel) : (channel.primaryModel ?? DEFAULT_PIECE_MODEL);
}
/**
 * The current build of the sibling already on the Board — what "is there an update" is asked
 * against, and what updating it downloads.
 *
 * **Never another model's build.** A Board holds one app per package id, so a sibling for other
 * pieces is not a newer version of the one installed: comparing against the head of all siblings
 * is what made a new Thrasos build an "update" to an Arcade copy. Changing pieces is Switch pieces.
 *
 * Null when this channel has nothing for that model — the team took it out, or the copy came from
 * another channel. There is no update for it here, and the installed game keeps its Play button.
 * With no `modelBuilds` there are no siblings to tell apart, so `latestBuild` is the head.
 */
export function headFor(channel, lastInstall) {
    const siblings = channel.modelBuilds ?? [];
    if (siblings.length === 0)
        return channel.latestBuild ?? null;
    const installed = installedModelKey(lastInstall, channel);
    return siblings.find((build) => modelKeyOf(build, channel) === installed) ?? null;
}
/**
 * **The one decision about which build an install, an update or an automatic update downloads.**
 *
 * Not on the Board: {@link resolveInstall}'s answer, unchanged. On the Board: {@link headFor} its
 * own sibling, as `ready`, or `unavailable` when this channel has nothing for that model. The
 * inventory is not consulted for an installed game, deliberately — the gate is on putting a game
 * on a Board that cannot run it, and one already there keeps being updated as what it is.
 */
export function installTarget(inventory, channel, installed, lastInstall) {
    if (!installed)
        return resolveInstall(inventory, channel);
    const head = headFor(channel, lastInstall);
    return head ? { kind: 'ready', build: head } : { kind: 'unavailable' };
}
/**
 * The siblings an installed game could be switched to — or empty, and then Switch pieces is not
 * offered at all. Never acted on by itself: changing pieces is always somebody pressing the button.
 *
 * - `pick`: every other sibling, pieces or not. Changing is what the channel is for.
 * - `auto` and `top`: what {@link resolveInstall} would install now, when that is another model.
 *
 * An unanswered Board is told to change pieces only when its copy is one this channel no longer
 * offers at all. With no `modelBuilds` there is no menu, so no switch.
 */
export function switchTargets(inventory, channel, lastInstall) {
    const siblings = channel.modelBuilds ?? [];
    if (siblings.length === 0)
        return [];
    const installed = installedModelKey(lastInstall, channel);
    if (channelModelChoice(channel) === 'pick') {
        return installCandidates(channel).filter((build) => modelKeyOf(build, channel) !== installed);
    }
    const choice = resolveInstall(inventory, channel);
    let target = null;
    if (choice.kind === 'ready')
        target = choice.build;
    else if (choice.kind === 'unconfigured' && !siblings.some((build) => modelKeyOf(build, channel) === installed)) {
        target = choice.build;
    }
    if (!target || modelKeyOf(target, channel) === installed)
        return [];
    return [target];
}
/** Whether the sibling on the Board is one the channel still offers — see {@link switchTargets}. */
export function installedOffered(channel, lastInstall) {
    const siblings = channel.modelBuilds ?? [];
    if (siblings.length === 0)
        return true;
    const installed = installedModelKey(lastInstall, channel);
    return siblings.some((build) => modelKeyOf(build, channel) === installed);
}
/**
 * The pieces a game's page names before play: what the build on the Board declared.
 *
 * A fact about the **installed** copy, not the channel's head. The ledger's own declaration first;
 * failing that, what the current build of the installed sibling declared — which for a game whose
 * builds never declared anything is nothing, and then nothing is named. Not on the Board, it is the
 * build an install would put there, so the two cannot disagree.
 */
export function launchModel(inventory, channel, installed, lastInstall) {
    if (installed) {
        return pieceModelNamed(lastInstall?.pieceModel ?? headFor(channel, lastInstall)?.pieceModel);
    }
    const choice = resolveInstall(inventory, channel);
    return choice.kind === 'ready' || choice.kind === 'unconfigured'
        ? pieceModelNamed(choice.build.pieceModel)
        : null;
}
//# sourceMappingURL=piece-models.js.map